import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppBarCMS } from '../../../../../basic/index.jsx';
import { CollapseBlock, Spinner } from '../../../../../common/index.jsx';
import FoWorkshopsForm from './FoWorkshopForm.jsx';
import {
  useGetWorkshopQuery,
  useGetWorkshopsQuery,
  useSaveWorkshopMutation,
} from '../../../../../../redux/services/fo_workshops.jsx';
import { useGetEventQuery } from '../../../../../../redux/services/fo_events.jsx';
import { useGetTeachersQuery } from '../../../../../../redux/services/users.jsx';
import { useSelector } from 'react-redux';
import { useToastMessages } from '../../../../../../hooks/useToastMessages';
import { routes } from '../../../../../../utils/helpers/ApiClient.jsx';

const WorkshopsEdit = ({ mode }) => {
  const { eventId, workshopId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);

  const { saveSuccess: showSaveSuccess, errorMessage } = useToastMessages();

  const isCreate = mode === 'create';
  const isEdit = mode === 'edit';

  const { data: workshopDetail, isLoading: isLoadingWorkshop } =
    useGetWorkshopQuery(workshopId, {
      skip: isCreate,
    });
  const { data: eventDetail, isLoading: isLoadingEvent } = useGetEventQuery(
    eventId,
    {
      skip: isEdit || !eventId,
    },
  );
  const { data: teachers, isLoading: isLoadingTeachers } =
    useGetTeachersQuery();

  const [saveWorkshop, { isSuccess: saveSuccess }] = useSaveWorkshopMutation();

  const { refetch } = useGetWorkshopsQuery(eventId);

  const teachersForSelect = useMemo(() => {
    if (!teachers) {
      return [];
    }

    return teachers
      .map((person) => ({
        label: person.full_name ?? person.fullName ?? '',
        value: person.person_id ?? person.id,
        last_name: person.last_name ?? person.person?.last_name ?? '',
      }))
      .sort((a, b) =>
        (a.last_name || a.label).localeCompare(b.last_name || b.label),
      );
  }, [teachers]);

  useEffect(() => {
    if (saveSuccess) {
      showSaveSuccess();
      navigate(-1);
      refetch();
    }
  }, [saveSuccess]);

  useEffect(() => {
    if (isEdit && workshopDetail?.event?.archived_at) {
      errorMessage({
        message: t('Seminár v archivovanej akcii nie je možné upravovať.'),
      });
      navigate(routes.cmsFoWorkshopsDetail(workshopId), { replace: true });
    }
  }, [
    errorMessage,
    isEdit,
    navigate,
    t,
    workshopDetail?.event?.archived_at,
    workshopId,
  ]);

  useEffect(() => {
    if (isCreate && eventDetail?.archived_at) {
      errorMessage({
        message: t('Do archivovanej akcie nie je možné pridávať semináre.'),
      });
      navigate(routes.cmsFoWorkshopsList(eventId), { replace: true });
    }
  }, [errorMessage, eventDetail?.archived_at, eventId, isCreate, navigate, t]);

  const title = isCreate ? t('Pridanie seminára') : t('Úprava seminára');

  if ((isEdit && isLoadingWorkshop) || isLoadingTeachers || isLoadingEvent) {
    return <Spinner />;
  }

  const initials = {
    name: undefined,
    description: undefined,
    lecturer_id: user.person_id,
    ...workshopDetail,
  };

  return (
    <>
      <AppBarCMS
        title={title}
        backUrl={-1}
        backUrlTooltip={t('Späť')}
        permissionsBasic="fo-workshops"
      />
      <div style={{ margin: '10px' }}>
        <CollapseBlock title={t('Obsah')}>
          <FoWorkshopsForm
            initials={initials}
            mode="edit"
            teachers={teachersForSelect}
            onSubmit={(values) => {
              saveWorkshop({
                data: values,
                workshopId,
                eventId,
              });
            }}
          />
        </CollapseBlock>
      </div>
    </>
  );
};

export default WorkshopsEdit;
